import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Progress,
  Select,
  SimpleGrid,
  Switch,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Badge,
  useToast,
} from "@chakra-ui/react";
import {
  countLocationSectionsProgress,
  EVENT_TYPE_OPTIONS,
  FACILITY_OPTIONS,
  FACING_OPTIONS,
  FURNISHING_OPTIONS,
  NETWORK_COVERAGE_OPTIONS,
  OWNERSHIP_OPTIONS,
  POWER_BACKUP_TYPES,
} from "../utils/locationProductDetails";

const PDF_MAX = 5 * 1024 * 1024;

const SECTIONS = [
  { id: "amenities", title: "Basic Amenities", icon: "fa-bolt" },
  { id: "facilities", title: "Facilities & Features", icon: "fa-building" },
  { id: "permissions", title: "Permissions & Legal", icon: "fa-file-contract" },
  { id: "space", title: "Space Details", icon: "fa-ruler-combined" },
  { id: "pricing", title: "Pricing & Deposit", icon: "fa-indian-rupee-sign" },
  { id: "media", title: "Media & Links", icon: "fa-link" },
  { id: "rules", title: "Rules & Restrictions", icon: "fa-ban" },
  { id: "parking", title: "Parking & Transport", icon: "fa-car" },
];

function ToggleRow({ label, isChecked, onChange }) {
  return (
    <FormControl display="flex" alignItems="center" justifyContent="space-between" py={1}>
      <FormLabel mb="0" fontWeight="500" fontSize="sm">
        {label}
      </FormLabel>
      <Switch isChecked={isChecked} onChange={(e) => onChange(e.target.checked)} colorScheme="brand" />
    </FormControl>
  );
}

/**
 * @param {object} props
 * @param {object} props.value location details object
 * @param {(v: object) => void} props.onChange
 * @param {number} props.tokenAmount
 * @param {(key: string, file: File | null) => void} props.onDocFile
 */
export default function LocationProductDetailsForm({ value, onChange, tokenAmount, onDocFile }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState([0]);

  const patch = (section, partial) => {
    onChange({ ...value, [section]: { ...value[section], ...partial } });
  };

  const toggleFacility = (name) => {
    const cur = new Set(value.facilities || []);
    if (cur.has(name)) cur.delete(name);
    else cur.add(name);
    onChange({ ...value, facilities: [...cur] });
  };

  const togglePowerType = (name) => {
    const cur = new Set(value.amenities?.powerBackupTypes || []);
    if (cur.has(name)) cur.delete(name);
    else cur.add(name);
    patch("amenities", { powerBackupTypes: [...cur] });
  };

  const toggleEventType = (name) => {
    const cur = new Set(value.rules?.eventTypesAllowed || []);
    if (cur.has(name)) cur.delete(name);
    else cur.add(name);
    patch("rules", { eventTypesAllowed: [...cur] });
  };

  const onPdfPick = (key, e) => {
    const file = e.target.files?.[0] || null;
    e.target.value = "";
    if (!file) {
      onDocFile(key, null);
      return;
    }
    if (file.type !== "application/pdf") {
      onDocFile(key, null);
      toast({ title: "PDF only", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    if (file.size > PDF_MAX) {
      onDocFile(key, null);
      toast({ title: "Max 5 MB per PDF", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    onDocFile(key, file);
  };

  const progress = useMemo(
    () => Math.round((countLocationSectionsProgress(value) / 8) * 100),
    [value]
  );

  const perm = value.permissions || {};
  const showLegalDocs = perm.permissionRequired !== false;
  const depositNum = Number(value.pricing?.securityDeposit);
  const tokenWarn =
    Number(tokenAmount) > 0 &&
    Number.isFinite(depositNum) &&
    depositNum < Number(tokenAmount);

  return (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      borderColor="gray.200"
      bg="gray.50"
      p={{ base: 3, md: 5 }}
      mt={6}
    >
      <HStack justify="space-between" mb={3} flexWrap="wrap" gap={2}>
        <HStack>
          <Box as="i" className="fa-solid fa-location-dot" color="brand.500" fontSize="lg" />
          <Text fontWeight="700" fontSize="md">
            Location listing details
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.600">
          {countLocationSectionsProgress(value)} / 8 sections started
        </Text>
      </HStack>
      <Progress value={progress} size="sm" colorScheme="brand" borderRadius="full" mb={4} />

      <Accordion allowMultiple index={expanded} onChange={(idx) => setExpanded(idx || [])}>
        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[0].icon}`} color="green.500" w="20px" />
                <Text fontWeight="600">{SECTIONS[0].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(0) ? (
              <VStack align="stretch" spacing={3}>
                <ToggleRow
                  label="Power backup available"
                  isChecked={!!value.amenities?.powerBackup}
                  onChange={(v) => patch("amenities", { powerBackup: v })}
                />
                {value.amenities?.powerBackup ? (
                  <Box>
                    <Text fontSize="xs" color="gray.600" mb={1}>
                      Backup type
                    </Text>
                    <Wrap>
                      {POWER_BACKUP_TYPES.map((t) => (
                        <WrapItem key={t}>
                          <Badge
                            as="button"
                            type="button"
                            px={3}
                            py={1}
                            borderRadius="full"
                            cursor="pointer"
                            colorScheme={
                              (value.amenities?.powerBackupTypes || []).includes(t) ? "brand" : "gray"
                            }
                            variant={
                              (value.amenities?.powerBackupTypes || []).includes(t) ? "solid" : "outline"
                            }
                            onClick={() => togglePowerType(t)}
                          >
                            {t}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                ) : null}
                <ToggleRow
                  label="Electric power available"
                  isChecked={!!value.amenities?.electricPowerAvailable}
                  onChange={(v) => patch("amenities", { electricPowerAvailable: v })}
                />
                {value.amenities?.electricPowerAvailable ? (
                  <FormControl>
                    <FormLabel fontSize="sm">Per unit cost (₹)</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      value={value.amenities?.electricPerUnitCost ?? ""}
                      onChange={(e) => patch("amenities", { electricPerUnitCost: e.target.value })}
                    />
                  </FormControl>
                ) : null}
                <ToggleRow
                  label="Water supply"
                  isChecked={!!value.amenities?.waterSupply}
                  onChange={(v) => patch("amenities", { waterSupply: v })}
                />
                <ToggleRow
                  label="WiFi"
                  isChecked={!!value.amenities?.wifi}
                  onChange={(v) => patch("amenities", { wifi: v })}
                />
                <ToggleRow
                  label="CCTV"
                  isChecked={!!value.amenities?.cctv}
                  onChange={(v) => patch("amenities", { cctv: v })}
                />
                <ToggleRow
                  label="Security 24×7"
                  isChecked={!!value.amenities?.security24x7}
                  onChange={(v) => patch("amenities", { security24x7: v })}
                />
              </VStack>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[1].icon}`} color="yellow.600" w="20px" />
                <Text fontWeight="600">{SECTIONS[1].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(1) ? (
              <Wrap spacing={2}>
                {FACILITY_OPTIONS.map((f) => (
                  <WrapItem key={f}>
                    <Badge
                      as="button"
                      type="button"
                      px={3}
                      py={1}
                      borderRadius="full"
                      cursor="pointer"
                      colorScheme={(value.facilities || []).includes(f) ? "brand" : "gray"}
                      variant={(value.facilities || []).includes(f) ? "solid" : "outline"}
                      onClick={() => toggleFacility(f)}
                    >
                      {f}
                    </Badge>
                  </WrapItem>
                ))}
              </Wrap>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[2].icon}`} color="blue.500" w="20px" />
                <Text fontWeight="600">{SECTIONS[2].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(2) ? (
              <VStack align="stretch" spacing={3}>
                <ToggleRow
                  label="Permission required for shoots / events"
                  isChecked={perm.permissionRequired !== false}
                  onChange={(v) => patch("permissions", { permissionRequired: v })}
                />
                {!showLegalDocs ? (
                  <Text fontSize="sm" color="gray.600">
                    Legal document uploads are hidden — enable “Permission required” if this location needs
                    compliance paperwork.
                  </Text>
                ) : (
                  <>
                    <ToggleRow
                      label="Location permission (local)"
                      isChecked={!!perm.locationPermission}
                      onChange={(v) => patch("permissions", { locationPermission: v })}
                    />
                    {perm.locationPermission ? (
                      <FormControl>
                        <FormLabel fontSize="sm">NOC certificate (PDF, required)</FormLabel>
                        <Input type="file" accept=".pdf,application/pdf" onChange={(e) => onPdfPick("noc", e)} />
                        {perm.noc_certificate ? (
                          <Text fontSize="xs" color="gray.600" mt={1}>
                            Saved: {String(perm.noc_certificate).split("/").pop()}
                          </Text>
                        ) : null}
                      </FormControl>
                    ) : null}
                    <FormControl>
                      <FormLabel fontSize="sm">Business license (PDF)</FormLabel>
                      <Input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => onPdfPick("business_license", e)}
                      />
                      {perm.business_license ? (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Saved: {String(perm.business_license).split("/").pop()}
                        </Text>
                      ) : null}
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Fire safety certificate (PDF)</FormLabel>
                      <Input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => onPdfPick("fire_safety", e)}
                      />
                      {perm.fire_safety_certificate ? (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Saved: {String(perm.fire_safety_certificate).split("/").pop()}
                        </Text>
                      ) : null}
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Local authority permission (PDF)</FormLabel>
                      <Input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => onPdfPick("local_authority", e)}
                      />
                      {perm.local_authority_permission ? (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Saved: {String(perm.local_authority_permission).split("/").pop()}
                        </Text>
                      ) : null}
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Insurance (optional PDF)</FormLabel>
                      <Input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(e) => onPdfPick("insurance", e)}
                      />
                      {perm.insurance ? (
                        <Text fontSize="xs" color="gray.600" mt={1}>
                          Saved: {String(perm.insurance).split("/").pop()}
                        </Text>
                      ) : null}
                    </FormControl>
                    <ToggleRow
                      label="Drone permission"
                      isChecked={!!perm.dronePermission}
                      onChange={(v) => patch("permissions", { dronePermission: v })}
                    />
                  </>
                )}
              </VStack>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[3].icon}`} color="purple.500" w="20px" />
                <Text fontWeight="600">{SECTIONS[3].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(3) ? (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm">Total area (sq ft)</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={value.space?.totalAreaSqFt ?? ""}
                    onChange={(e) => patch("space", { totalAreaSqFt: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Seating capacity</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={value.space?.seatingCapacity ?? ""}
                    onChange={(e) => patch("space", { seatingCapacity: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Standing capacity</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={value.space?.standingCapacity ?? ""}
                    onChange={(e) => patch("space", { standingCapacity: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Furnishing type</FormLabel>
                  <Select
                    placeholder="Select"
                    value={value.space?.furnishingType ?? ""}
                    onChange={(e) => patch("space", { furnishingType: e.target.value })}
                  >
                    {FURNISHING_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Number of rooms</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={value.space?.numberOfRooms ?? ""}
                    onChange={(e) => patch("space", { numberOfRooms: e.target.value })}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                  <FormLabel mb="0" fontSize="sm">
                    Stay available
                  </FormLabel>
                  <Switch
                    isChecked={!!value.space?.stayAvailable}
                    onChange={(e) => patch("space", { stayAvailable: e.target.checked })}
                    colorScheme="brand"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Floor number</FormLabel>
                  <Input
                    type="number"
                    value={value.space?.floorNumber ?? ""}
                    onChange={(e) => patch("space", { floorNumber: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Total floors</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={value.space?.totalFloors ?? ""}
                    onChange={(e) => patch("space", { totalFloors: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Facing direction</FormLabel>
                  <Select
                    placeholder="Select"
                    value={value.space?.facingDirection ?? ""}
                    onChange={(e) => patch("space", { facingDirection: e.target.value })}
                  >
                    {FACING_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Ownership type</FormLabel>
                  <Select
                    placeholder="Select"
                    value={value.space?.ownershipType ?? ""}
                    onChange={(e) => patch("space", { ownershipType: e.target.value })}
                  >
                    {OWNERSHIP_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className="fa-solid fa-tag" color="orange.500" w="20px" />
                <Text fontWeight="600">{SECTIONS[4].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(4) ? (
              <VStack align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Security deposit (₹)</FormLabel>
                  <Input
                    type="number"
                    min={0}
                    value={value.pricing?.securityDeposit ?? ""}
                    onChange={(e) => patch("pricing", { securityDeposit: e.target.value })}
                  />
                  {Number(tokenAmount) > 0 ? (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Listing token on your plan: ₹{tokenAmount}. Deposit should be ≥ token.
                    </Text>
                  ) : null}
                  {tokenWarn ? (
                    <Text fontSize="sm" color="orange.600" mt={2} fontWeight="600">
                      Security deposit is below the token amount — adjust before saving.
                    </Text>
                  ) : null}
                </FormControl>
              </VStack>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[5].icon}`} color="red.500" w="20px" />
                <Text fontWeight="600">{SECTIONS[5].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(5) ? (
              <FormControl>
                <FormLabel fontSize="sm">Video link (YouTube / Vimeo)</FormLabel>
                <Input
                  type="url"
                  placeholder="https://"
                  value={value.media?.videoLink ?? ""}
                  onChange={(e) => patch("media", { videoLink: e.target.value })}
                />
              </FormControl>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[6].icon}`} color="gray.700" w="20px" />
                <Text fontWeight="600">{SECTIONS[6].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(6) ? (
              <VStack align="stretch" spacing={3}>
                <ToggleRow
                  label="Smoking allowed"
                  isChecked={!!value.rules?.smokingAllowed}
                  onChange={(v) => patch("rules", { smokingAllowed: v })}
                />
                <ToggleRow
                  label="Alcohol allowed"
                  isChecked={!!value.rules?.alcoholAllowed}
                  onChange={(v) => patch("rules", { alcoholAllowed: v })}
                />
                <ToggleRow
                  label="Loud music allowed"
                  isChecked={!!value.rules?.loudMusicAllowed}
                  onChange={(v) => patch("rules", { loudMusicAllowed: v })}
                />
                <ToggleRow
                  label="Pets allowed"
                  isChecked={!!value.rules?.petsAllowed}
                  onChange={(v) => patch("rules", { petsAllowed: v })}
                />
                <FormControl>
                  <FormLabel fontSize="sm">Closing time / curfew</FormLabel>
                  <Input
                    type="time"
                    value={value.rules?.closingTime ?? ""}
                    onChange={(e) => patch("rules", { closingTime: e.target.value })}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Mobile network coverage</FormLabel>
                  <Select
                    placeholder="Select"
                    value={value.rules?.mobileNetworkCoverage ?? ""}
                    onChange={(e) => patch("rules", { mobileNetworkCoverage: e.target.value })}
                  >
                    {NETWORK_COVERAGE_OPTIONS.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={1}>
                    Event types allowed
                  </Text>
                  <Wrap>
                    {EVENT_TYPE_OPTIONS.map((t) => (
                      <WrapItem key={t}>
                        <Badge
                          as="button"
                          type="button"
                          px={3}
                          py={1}
                          borderRadius="full"
                          cursor="pointer"
                          colorScheme={
                            (value.rules?.eventTypesAllowed || []).includes(t) ? "brand" : "gray"
                          }
                          variant={
                            (value.rules?.eventTypesAllowed || []).includes(t) ? "solid" : "outline"
                          }
                          onClick={() => toggleEventType(t)}
                        >
                          {t}
                        </Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              </VStack>
            ) : null}
          </AccordionPanel>
        </AccordionItem>

        <AccordionItem border="none" mb={2} bg="white" borderRadius="lg" boxShadow="sm">
          <h2>
            <AccordionButton borderRadius="lg" py={3}>
              <HStack flex="1" textAlign="left" spacing={3}>
                <Box as="i" className={`fa-solid ${SECTIONS[7].icon}`} color="teal.600" w="20px" />
                <Text fontWeight="600">{SECTIONS[7].title}</Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={4} pt={0} px={4}>
            {expanded.includes(7) ? (
              <VStack align="stretch" spacing={3}>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl>
                    <FormLabel fontSize="sm">2-wheeler parking</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      value={value.parking?.parking2Wheeler ?? ""}
                      onChange={(e) => patch("parking", { parking2Wheeler: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">4-wheeler parking</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      value={value.parking?.parking4Wheeler ?? ""}
                      onChange={(e) => patch("parking", { parking4Wheeler: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">8-wheeler parking</FormLabel>
                    <Input
                      type="number"
                      min={0}
                      value={value.parking?.parking8Wheeler ?? ""}
                      onChange={(e) => patch("parking", { parking8Wheeler: e.target.value })}
                    />
                  </FormControl>
                </SimpleGrid>
                <ToggleRow
                  label="Helipad available"
                  isChecked={!!value.parking?.helipadAvailable}
                  onChange={(v) => patch("parking", { helipadAvailable: v })}
                />
                <ToggleRow
                  label="Private air strip / runway"
                  isChecked={!!value.parking?.privateAirRunway}
                  onChange={(v) => patch("parking", { privateAirRunway: v })}
                />
              </VStack>
            ) : null}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}
